import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { fmtDate } from '@/lib/format';
import { Plus, KeyRound, Trash2, Pencil, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';
import { isPasswordStrong, PASSWORD_POLICY_MESSAGE } from '@/lib/passwordPolicy';

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [profiles, setProfiles] = useState({ owners: [], consultants: [], tenants: [] });
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState('');
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'tenant', profile_id: '' });

  const load = () => api.get('/users').then(r => setItems(r.data));
  useEffect(() => {
    load();
    Promise.all([api.get('/owners'), api.get('/consultants'), api.get('/tenants')])
      .then(([a, b, c]) => setProfiles({ owners: a.data, consultants: b.data, tenants: c.data }));
  }, []);
  const profileList = form.role === 'owner' ? profiles.owners : form.role === 'consultant' ? profiles.consultants : form.role === 'tenant' ? profiles.tenants : [];

  const create = async () => {
    if (!isPasswordStrong(form.password)) { toast.error(PASSWORD_POLICY_MESSAGE); return; }
    try {
      await api.post('/auth/register', { ...form, profile_id: form.profile_id || null });
      toast.success('User created'); setOpen(false);
      setForm({ email: '', name: '', password: '', role: 'tenant', profile_id: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async () => {
    if (edit.password && !isPasswordStrong(edit.password)) { toast.error(PASSWORD_POLICY_MESSAGE); return; }
    try {
      const payload = { name: edit.name, email: edit.email, role: edit.role };
      if (edit.password) payload.password = edit.password;
      await api.put(`/users/${edit.id}`, payload);
      toast.success('User updated'); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success('User deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const filtered = q ? items.filter(u => `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q.toLowerCase())) : items;

  return (
    <div>
      <PageHeader title="Users" subtitle="Onboard users, change credentials, assign roles" actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-user-button"><Plus className="h-4 w-4 mr-1" /> New user</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Onboard user</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" /></div>
              <div className="sm:col-span-2"><PasswordRequirements value={form.password} /></div>
              <div><Label>Role</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, profile_id: '' })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="admin">Super Admin</SelectItem><SelectItem value="owner">Owner</SelectItem><SelectItem value="consultant">Consultant</SelectItem><SelectItem value="agent">Agent</SelectItem><SelectItem value="tenant">Tenant</SelectItem></SelectContent></Select></div>
              <div><Label>Linked profile</Label><Select value={form.profile_id} onValueChange={(v) => setForm({ ...form, profile_id: v })} disabled={form.role === 'admin'}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{profileList.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="user-submit-button">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />

      <div className="mb-3 max-w-md relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-8" placeholder="Search by name, email or role" value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" />
      </div>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/60"><tr>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Name</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Email</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Role</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Created</th>
            <th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t hover:bg-muted/40">
                <td className="px-3 py-2 font-medium">{u.name}</td>
                <td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2 capitalize">{u.role}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(u.created_at)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setEdit({ ...u, password: '' })} data-testid={`edit-user-${u.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  {u.id !== me?.id && (
                    <Button size="sm" variant="ghost" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  )}
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">No users.</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* Edit user credentials dialog */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Edit user credentials</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="sm:col-span-2"><Label>Name</Label><Input value={edit.name || ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-user-name" /></div>
              <div className="sm:col-span-2"><Label>Email</Label><Input type="email" value={edit.email || ''} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="edit-user-email" /></div>
              <div className="sm:col-span-2">
                <Label>New password <span className="text-muted-foreground">(leave blank to keep existing)</span></Label>
                <Input type="password" value={edit.password || ''} onChange={(e) => setEdit({ ...edit, password: e.target.value })} placeholder="At least 8 characters" data-testid="edit-user-password" />
              </div>
              {edit.password ? (
                <div className="sm:col-span-2"><PasswordRequirements value={edit.password} /></div>
              ) : null}
              <div className="sm:col-span-2"><Label>Role</Label>
                <Select value={edit.role} onValueChange={(v) => setEdit({ ...edit, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Super Admin</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="consultant">Consultant</SelectItem>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="tenant">Tenant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {edit.id === me?.id && (
                <div className="sm:col-span-2 text-xs rounded-lg border bg-amber-50/60 px-3 py-2 text-amber-900">
                  You are editing your own account. If you change the email or password, you'll need to sign in again.
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="edit-user-save">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
